import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { COMPANY_INFO, NAVIGATION_ITEMS } from "../../../data/company";
import { scrollToElement } from "../../../utils/helpers";
import Button from "../../ui/Button/Button";
import LanguageToggle from "../../ui/LanguageToggle/LanguageToggle";
import { useLanguage } from "../../../context/LanguageContext";
import "./Header.css";

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    if (href.startsWith('/')) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    const elementId = href.replace('#', '');
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToElement(elementId), 0);
    } else {
      scrollToElement(elementId);
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const { t } = useLanguage();

  return (
    <header className={`header ${isScrolled ? "header--scrolled" : ""}`}>
      {/* Top Bar */}
      <div className="header__top-bar">
        <div className="container">
          <div className="header__contact-info">
            <div className="header__contact-item">
              <i className="fas fa-envelope"></i>
              <span>{COMPANY_INFO.contact.email[0]}</span>
            </div>
            <div className="header__contact-item">
              <i className="fas fa-phone"></i>
              <span>{COMPANY_INFO.contact.phone[0]}</span>
            </div>
            <div className="header__contact-item header__contact-item--desktop-only">
              <i className="fas fa-clock"></i>
              <span>{t('company.hours.weekdays')}</span>
            </div>
          </div>

          <div className="header__social-links">
            <LanguageToggle />
            <a
              href={COMPANY_INFO.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              href={COMPANY_INFO.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin-in"></i>
            </a>
            <a
              href={COMPANY_INFO.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="header__main">
        <div className="container">
          <div className="header__content">
            {/* Logo (image) */}
            <div
              className="header__logo"
              onClick={() => handleNavClick("#hero")}
            >
              <img
                className="header__logo-image"
                src={`${process.env.PUBLIC_URL}/logo1.png`}
                alt="ESF OTOMOTİV"
                loading="eager"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="header__nav">
              <ul className="header__nav-list">
                {NAVIGATION_ITEMS.map((item) => (
                  <li key={item.id} className="header__nav-item">
                    <button
                      className="header__nav-link"
                      onClick={() => handleNavClick(item.href)}
                    >
                      {t(`nav.${item.id}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* CTA Button */}
            <div className="header__cta">
              <Button
                variant="primary"
                size="md"
                onClick={() => handleNavClick("#contact")}
                leftIcon={<i className="fas fa-phone"></i>}
              >
                {t('header.cta')}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`header__mobile-toggle ${
                isMobileMenuOpen ? "header__mobile-toggle--active" : ""
              }`}
              onClick={toggleMobileMenu}
              aria-label={t('header.menuToggle')}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`header__mobile-menu ${
          isMobileMenuOpen ? "header__mobile-menu--open" : ""
        }`}
      >
        <div className="header__mobile-menu-content">
          <button 
            className="header__mobile-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <i className="fas fa-times"></i>
          </button>

          <nav className="header__mobile-nav">
            <ul className="header__mobile-nav-list">
              {NAVIGATION_ITEMS.map((item) => (
                <li key={item.id} className="header__mobile-nav-item">
                  <button
                    className="header__mobile-nav-link"
                    onClick={() => handleNavClick(item.href)}
                  >
                    {t(`nav.${item.id}`)}
                  </button>
                </li>
              ))}
              </ul>
            </nav>

          <div className="header__mobile-contact">
            <div className="header__mobile-contact-item">
              <i className="fas fa-phone"></i>
              <span>{COMPANY_INFO.contact.phone[0]}</span>
            </div>
            <div className="header__mobile-contact-item">
              <i className="fas fa-envelope"></i>
              <span>{COMPANY_INFO.contact.email[0]}</span>
            </div>
          </div>

          <div className="header__mobile-social">
            <a
              href={COMPANY_INFO.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              href={COMPANY_INFO.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin-in"></i>
            </a>
            <a
              href={COMPANY_INFO.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="header__mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
